import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import {
  ArrowLeft,
  Plus,
  Pencil,
  Trash2,
  Building2,
  Loader2,
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Image as ImageIcon,
  StickyNote,
  Upload,
  Download,
  Send,
  ChevronDown,
  ExternalLink,
  Copy,
  Check,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CompanyDetail = () => {
  const { companyId } = useParams();
  const navigate = useNavigate();
  const { isOfficeManager } = useAuth();
  
  const [company, setCompany] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [files, setFiles] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  
  // Dialog states
  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [noteDialogOpen, setNoteDialogOpen] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  
  const [editingAddress, setEditingAddress] = useState(null);
  const [editingContact, setEditingContact] = useState(null);
  const [selectedContact, setSelectedContact] = useState(null);
  
  const [saving, setSaving] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(null);
  
  // Forms
  const [addressForm, setAddressForm] = useState({
    building_name: "",
    street_address: "",
    city: "",
    province: "",
    postal_code: "",
    job_name: "",
    is_primary: false,
    notes: "",
  });
  
  const [contactForm, setContactForm] = useState({
    name: "",
    email: "",
    phone: "",
    title: "",
    notes: "",
    address_ids: [],
  });
  
  const [noteContent, setNoteContent] = useState("");
  const [uploadFiles, setUploadFiles] = useState([]);
  const [uploadType, setUploadType] = useState("document");

  const fetchCompanyData = useCallback(async () => {
    try {
      const [companyRes, templatesRes] = await Promise.all([
        axios.get(`${API}/companies/${companyId}/full`),
        axios.get(`${API}/templates`),
      ]);
      
      setCompany(companyRes.data);
      setAddresses(companyRes.data.addresses || []);
      setContacts(companyRes.data.contacts || []);
      setFiles(companyRes.data.files || []);
      setTemplates(templatesRes.data || []);
    } catch (error) {
      toast.error("Failed to load company data");
      navigate("/companies");
    } finally {
      setLoading(false);
    }
  }, [companyId, navigate]);

  useEffect(() => {
    fetchCompanyData();
  }, [fetchCompanyData]);

  // Address handlers
  const openAddressDialog = (address = null) => {
    if (address) {
      setEditingAddress(address);
      setAddressForm({
        building_name: address.building_name || "",
        street_address: address.street_address || "",
        city: address.city || "",
        province: address.province || "",
        postal_code: address.postal_code || "",
        job_name: address.job_name || "",
        is_primary: address.is_primary || false,
        notes: address.notes || "",
      });
    } else {
      setEditingAddress(null);
      setAddressForm({
        building_name: "",
        street_address: "",
        city: "",
        province: "",
        postal_code: "",
        job_name: "",
        is_primary: false,
        notes: "",
      });
    }
    setAddressDialogOpen(true);
  };

  const handleSubmitAddress = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...addressForm, company_id: companyId };
      if (editingAddress) {
        await axios.put(`${API}/company-addresses/${editingAddress.id}`, payload);
        toast.success("Address updated");
      } else {
        await axios.post(`${API}/company-addresses`, payload);
        toast.success("Address created");
      }
      setAddressDialogOpen(false);
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAddress = async (addressId) => {
    if (!window.confirm("Delete this address?")) return;
    try {
      await axios.delete(`${API}/company-addresses/${addressId}`);
      toast.success("Address deleted");
      fetchCompanyData();
    } catch (error) {
      toast.error("Failed to delete address");
    }
  };

  // Contact handlers
  const openContactDialog = (contact = null) => {
    if (contact) {
      setEditingContact(contact);
      setContactForm({
        name: contact.name || "",
        email: contact.email || "",
        phone: contact.phone || "",
        title: contact.title || "",
        notes: contact.notes || "",
        address_ids: contact.address_ids || [],
      });
    } else {
      setEditingContact(null);
      setContactForm({
        name: "",
        email: "",
        phone: "",
        title: "",
        notes: "",
        address_ids: [],
      });
    }
    setContactDialogOpen(true);
  };

  const handleSubmitContact = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = { ...contactForm, company_id: companyId };
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, payload);
        toast.success("Contact updated");
      } else {
        await axios.post(`${API}/contacts`, payload);
        toast.success("Contact created");
      }
      setContactDialogOpen(false);
      fetchCompanyData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm("Delete this contact?")) return;
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      toast.success("Contact deleted");
      fetchCompanyData();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  // Email handlers
  const openEmailDialog = (contact) => {
    setSelectedContact(contact);
    setEmailDialogOpen(true);
  };

  const handleSendBlankEmail = (email) => {
    window.location.href = `mailto:${email}`;
    setEmailDialogOpen(false);
  };

  const handleSendTemplateEmail = async (templateId) => {
    // This would send the template via the API
    toast.info("Template email feature - would send template to " + selectedContact?.email);
    setEmailDialogOpen(false);
  };

  const handleCopyEmail = async (email) => {
    await navigator.clipboard.writeText(email);
    setCopiedEmail(email);
    toast.success("Email copied to clipboard");
    setTimeout(() => setCopiedEmail(null), 2000);
  };

  // Note handlers
  const handleCreateNote = async () => {
    if (!noteContent.trim()) {
      toast.error("Please enter note content");
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("company_id", companyId);
      formData.append("file_type", "note");
      formData.append("content", noteContent);
      
      await axios.post(`${API}/company-files`, formData);
      toast.success("Note created");
      setNoteDialogOpen(false);
      setNoteContent("");
      fetchCompanyData();
    } catch (error) {
      toast.error("Failed to create note");
    } finally {
      setSaving(false);
    }
  };

  // File upload handlers
  const handleFileUpload = async () => {
    if (uploadFiles.length === 0) {
      toast.error("Please select files to upload");
      return;
    }
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("company_id", companyId);
      formData.append("file_type", uploadType);
      uploadFiles.forEach((file) => formData.append("files", file));
      
      await axios.post(`${API}/mass-upload/documents`, formData);
      toast.success(`${uploadFiles.length} files uploaded`);
      setUploadDialogOpen(false);
      setUploadFiles([]);
      fetchCompanyData();
    } catch (error) {
      toast.error("Failed to upload files");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteFile = async (fileId) => {
    if (!window.confirm("Delete this file?")) return;
    try {
      await axios.delete(`${API}/company-files/${fileId}`);
      toast.success("File deleted");
      fetchCompanyData();
    } catch (error) {
      toast.error("Failed to delete file");
    }
  };

  const handleAddressToggle = (addressId) => {
    setContactForm(prev => ({
      ...prev,
      address_ids: prev.address_ids.includes(addressId)
        ? prev.address_ids.filter(id => id !== addressId)
        : [...prev.address_ids, addressId]
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!company) return null;

  const getFileIcon = (fileType) => {
    switch (fileType) {
      case "photo": return <ImageIcon className="h-4 w-4" />;
      case "note": return <StickyNote className="h-4 w-4" />;
      default: return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6 animate-in" data-testid="company-detail-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate("/companies")} data-testid="back-btn">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="h-6 w-6 text-primary" />
            </div>
            <div>
              <h1 className="font-heading text-2xl font-bold">{company.name}</h1>
              <p className="text-sm text-muted-foreground flex items-center gap-1">
                <MapPin className="h-3 w-3" />
                {company.address}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Info */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Addresses</p>
            <p className="text-2xl font-bold">{addresses.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Contacts</p>
            <p className="text-2xl font-bold">{contacts.length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Documents</p>
            <p className="text-2xl font-bold">{files.filter(f => f.file_type === "document").length}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm text-muted-foreground">Photos</p>
            <p className="text-2xl font-bold">{files.filter(f => f.file_type === "photo").length}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="addresses" data-testid="tab-addresses">Addresses</TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">Contacts</TabsTrigger>
          <TabsTrigger value="files" data-testid="tab-files">Files & Notes</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Company Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {company.phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    {company.phone}
                  </div>
                )}
                {company.accounts_payable_email && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <a href={`mailto:${company.accounts_payable_email}`} className="text-primary hover:underline">
                      {company.accounts_payable_email}
                    </a>
                  </div>
                )}
                {company.secondary_address && (
                  <div className="flex items-center gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-muted-foreground" />
                    {company.secondary_address}
                  </div>
                )}
                {company.notes && (
                  <div className="mt-4 p-3 bg-muted rounded-lg text-sm">
                    <p className="font-medium mb-1">Notes</p>
                    {company.notes}
                  </div>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Recent Contacts</CardTitle>
              </CardHeader>
              <CardContent>
                {contacts.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No contacts yet</p>
                ) : (
                  <div className="space-y-2">
                    {contacts.slice(0, 3).map((contact) => (
                      <div key={contact.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-muted/50">
                        <div className="flex items-center gap-2">
                          <div className="h-8 w-8 rounded-full bg-secondary/30 flex items-center justify-center">
                            <User className="h-4 w-4" />
                          </div>
                          <div>
                            <p className="text-sm font-medium">{contact.name}</p>
                            <p className="text-xs text-muted-foreground">{contact.title || contact.email}</p>
                          </div>
                        </div>
                        <Button variant="ghost" size="icon" onClick={() => openEmailDialog(contact)}>
                          <Mail className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Addresses Tab */}
        <TabsContent value="addresses" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Job Locations / Addresses</h3>
            {isOfficeManager && (
              <Button onClick={() => openAddressDialog()} data-testid="add-address-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Address
              </Button>
            )}
          </div>

          {addresses.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No addresses added yet
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 gap-4">
              {addresses.map((address) => (
                <Card key={address.id} className={address.is_primary ? "border-primary" : ""}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div>
                        {address.building_name && (
                          <CardTitle className="text-base">{address.building_name}</CardTitle>
                        )}
                        <CardDescription className="mt-1">
                          {address.street_address}
                          {address.city && `, ${address.city}`}
                          {address.province && `, ${address.province}`}
                          {address.postal_code && ` ${address.postal_code}`}
                        </CardDescription>
                      </div>
                      {address.is_primary && <Badge>Primary</Badge>}
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {address.job_name && (
                      <p className="text-sm">
                        <span className="text-muted-foreground">Job Name (Internal): </span>
                        <span className="font-medium">{address.job_name}</span>
                      </p>
                    )}
                    {address.notes && (
                      <p className="text-sm text-muted-foreground">{address.notes}</p>
                    )}
                    {address.photos && address.photos.length > 0 && (
                      <div className="flex gap-2 mt-2">
                        {address.photos.slice(0, 3).map((photo, idx) => (
                          <div key={idx} className="h-12 w-12 rounded bg-muted flex items-center justify-center">
                            <ImageIcon className="h-4 w-4 text-muted-foreground" />
                          </div>
                        ))}
                        {address.photos.length > 3 && (
                          <div className="h-12 w-12 rounded bg-muted flex items-center justify-center text-sm">
                            +{address.photos.length - 3}
                          </div>
                        )}
                      </div>
                    )}
                    {isOfficeManager && (
                      <div className="flex gap-2 pt-2">
                        <Button variant="outline" size="sm" onClick={() => openAddressDialog(address)}>
                          <Pencil className="h-3 w-3 mr-1" /> Edit
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="text-destructive"
                          onClick={() => handleDeleteAddress(address.id)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" /> Delete
                        </Button>
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Contacts Tab */}
        <TabsContent value="contacts" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Contacts</h3>
            {isOfficeManager && (
              <Button onClick={() => openContactDialog()} data-testid="add-contact-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Contact
              </Button>
            )}
          </div>

          {contacts.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No contacts added yet
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {contacts.map((contact) => (
                <Card key={contact.id}>
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="h-5 w-5 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-base">{contact.name}</CardTitle>
                          {contact.title && (
                            <CardDescription>{contact.title}</CardDescription>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    <div className="flex items-center gap-2 text-sm">
                      <Mail className="h-4 w-4 text-muted-foreground" />
                      <span className="flex-1 truncate">{contact.email}</span>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-6 w-6"
                        onClick={() => handleCopyEmail(contact.email)}
                      >
                        {copiedEmail === contact.email ? (
                          <Check className="h-3 w-3 text-green-500" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </Button>
                    </div>
                    {contact.phone && (
                      <div className="flex items-center gap-2 text-sm">
                        <Phone className="h-4 w-4 text-muted-foreground" />
                        {contact.phone}
                      </div>
                    )}
                    {contact.address_ids && contact.address_ids.length > 0 && (
                      <div className="flex items-center gap-2 text-sm">
                        <MapPin className="h-4 w-4 text-muted-foreground" />
                        <span>{contact.address_ids.length} location(s)</span>
                      </div>
                    )}
                    
                    <div className="flex gap-2 pt-2">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="outline" size="sm" className="flex-1">
                            <Mail className="h-3 w-3 mr-1" /> Email
                            <ChevronDown className="h-3 w-3 ml-1" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => handleSendBlankEmail(contact.email)}>
                            <ExternalLink className="h-4 w-4 mr-2" />
                            Open Blank Email
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => openEmailDialog(contact)}>
                            <FileText className="h-4 w-4 mr-2" />
                            Use Template
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                      
                      {isOfficeManager && (
                        <>
                          <Button variant="outline" size="icon" onClick={() => openContactDialog(contact)}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button 
                            variant="outline" 
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDeleteContact(contact.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Files Tab */}
        <TabsContent value="files" className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Files, Photos & Notes</h3>
            {isOfficeManager && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setNoteDialogOpen(true)} data-testid="add-note-btn">
                  <StickyNote className="h-4 w-4 mr-2" />
                  Add Note
                </Button>
                <Button onClick={() => setUploadDialogOpen(true)} data-testid="upload-files-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Files
                </Button>
              </div>
            )}
          </div>

          {files.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-muted-foreground">
                No files or notes yet
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {files.map((file) => (
                <Card key={file.id}>
                  <CardContent className="py-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-lg bg-muted flex items-center justify-center">
                          {getFileIcon(file.file_type)}
                        </div>
                        <div>
                          <p className="font-medium text-sm">{file.file_name}</p>
                          <p className="text-xs text-muted-foreground">
                            {new Date(file.created_at).toLocaleDateString()} • {file.file_type}
                          </p>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        {file.file_type === "note" ? (
                          <Button variant="outline" size="sm" onClick={() => {
                            toast.info(file.content || "No content");
                          }}>
                            View
                          </Button>
                        ) : file.file_path && (
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => window.open(`${API}/company-files/${file.id}/download`, '_blank')}
                          >
                            <Download className="h-3 w-3 mr-1" /> Download
                          </Button>
                        )}
                        {isOfficeManager && (
                          <Button 
                            variant="outline" 
                            size="icon"
                            className="text-destructive"
                            onClick={() => handleDeleteFile(file.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </div>
                    {file.file_type === "note" && file.content && (
                      <div className="mt-3 p-3 bg-muted rounded text-sm">
                        {file.content}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Address Dialog */}
      <Dialog open={addressDialogOpen} onOpenChange={setAddressDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingAddress ? "Edit Address" : "Add New Address"}</DialogTitle>
            <DialogDescription>Enter the address details for this job location.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitAddress} className="space-y-4">
            <div className="space-y-2">
              <Label>Building Name</Label>
              <Input
                value={addressForm.building_name}
                onChange={(e) => setAddressForm({ ...addressForm, building_name: e.target.value })}
                placeholder="e.g., Sunset Tower"
              />
            </div>
            <div className="space-y-2">
              <Label>Street Address *</Label>
              <Input
                value={addressForm.street_address}
                onChange={(e) => setAddressForm({ ...addressForm, street_address: e.target.value })}
                required
                placeholder="e.g., 123 Main Street"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City</Label>
                <Input
                  value={addressForm.city}
                  onChange={(e) => setAddressForm({ ...addressForm, city: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Province</Label>
                <Input
                  value={addressForm.province}
                  onChange={(e) => setAddressForm({ ...addressForm, province: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Postal Code</Label>
              <Input
                value={addressForm.postal_code}
                onChange={(e) => setAddressForm({ ...addressForm, postal_code: e.target.value })}
              />
            </div>
            <div className="space-y-2">
              <Label>Job Name (Internal Only)</Label>
              <Input
                value={addressForm.job_name}
                onChange={(e) => setAddressForm({ ...addressForm, job_name: e.target.value })}
                placeholder="e.g., Annual Spring Clean"
              />
            </div>
            <div className="flex items-center gap-2">
              <Checkbox
                id="is_primary"
                checked={addressForm.is_primary}
                onCheckedChange={(checked) => setAddressForm({ ...addressForm, is_primary: checked })}
              />
              <Label htmlFor="is_primary" className="text-sm">Primary Address</Label>
            </div>
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={addressForm.notes}
                onChange={(e) => setAddressForm({ ...addressForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setAddressDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingAddress ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Contact Dialog */}
      <Dialog open={contactDialogOpen} onOpenChange={setContactDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editingContact ? "Edit Contact" : "Add New Contact"}</DialogTitle>
            <DialogDescription>Enter the contact details.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitContact} className="space-y-4">
            <div className="space-y-2">
              <Label>Full Name *</Label>
              <Input
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Email *</Label>
              <Input
                type="email"
                value={contactForm.email}
                onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                required
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input
                  value={contactForm.phone}
                  onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={contactForm.title}
                  onChange={(e) => setContactForm({ ...contactForm, title: e.target.value })}
                  placeholder="e.g., Property Manager"
                />
              </div>
            </div>
            
            {addresses.length > 0 && (
              <div className="space-y-2">
                <Label>Associated Addresses</Label>
                <div className="space-y-2 max-h-32 overflow-y-auto border rounded-lg p-2">
                  {addresses.map((addr) => (
                    <div key={addr.id} className="flex items-center gap-2">
                      <Checkbox
                        id={`addr-${addr.id}`}
                        checked={contactForm.address_ids.includes(addr.id)}
                        onCheckedChange={() => handleAddressToggle(addr.id)}
                      />
                      <Label htmlFor={`addr-${addr.id}`} className="text-sm font-normal cursor-pointer">
                        {addr.building_name || addr.street_address}
                      </Label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Textarea
                value={contactForm.notes}
                onChange={(e) => setContactForm({ ...contactForm, notes: e.target.value })}
                rows={2}
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setContactDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {editingContact ? "Update" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Email Template Dialog */}
      <Dialog open={emailDialogOpen} onOpenChange={setEmailDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Email {selectedContact?.name}</DialogTitle>
            <DialogDescription>
              Choose how to send an email to {selectedContact?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button 
              variant="outline" 
              className="w-full justify-start"
              onClick={() => handleSendBlankEmail(selectedContact?.email)}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Blank Email in Mail Client
            </Button>
            
            {templates.length > 0 && (
              <>
                <div className="text-sm font-medium">Or use a template:</div>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {templates.map((template) => (
                    <Button
                      key={template.id}
                      variant="outline"
                      className="w-full justify-start"
                      onClick={() => handleSendTemplateEmail(template.id)}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      {template.name}
                    </Button>
                  ))}
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Note Dialog */}
      <Dialog open={noteDialogOpen} onOpenChange={setNoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Note</DialogTitle>
            <DialogDescription>Create a note for this company.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="Enter your note..."
              rows={5}
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setNoteDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateNote} disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Save Note
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Upload Files</DialogTitle>
            <DialogDescription>Upload documents or photos for this company.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>File Type</Label>
              <Select value={uploadType} onValueChange={setUploadType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="document">Document</SelectItem>
                  <SelectItem value="photo">Photo</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Select Files</Label>
              <Input
                type="file"
                multiple
                onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                accept={uploadType === "photo" ? "image/*" : "*"}
              />
              {uploadFiles.length > 0 && (
                <p className="text-sm text-muted-foreground">
                  {uploadFiles.length} file(s) selected
                </p>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleFileUpload} disabled={saving || uploadFiles.length === 0}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Upload
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CompanyDetail;
